import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  FlashMessage,
  Grid,
  IconName,
  ProColors,
} from '@treatwell/treatwell-aura';
import { css } from '@emotion/react';
import { FormContextData, useFormContext } from '../../state';
import { BusinessType as BusinessTypeType } from '../../api-client';
import { getCountryCodeFromTLD } from '../../helpers';
import { CardHorizontal, CardVertical } from './components';
import { useValidator } from './helpers/use-validation';
import { ForcedCheckbox } from './components/forced-checkbox';

type BusinessTypeCardProps = {
  icon: IconName;
  type: BusinessTypeType;
};

function DedicatedSpace(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'businessType' });
  const { data, setData } = useFormContext();

  const validate = useCallback(
    (_data: FormContextData) =>
      _data.businessType !== BusinessTypeType.HOME || !!_data.dedicatedSpace
        ? null
        : 'DedicatedSpace is not checked',
    [],
  );

  return (
    <ForcedCheckbox
      onChange={value => {
        setData(current => ({ ...current, dedicatedSpace: value }));
      }}
      checked={data.dedicatedSpace || false}
      validate={validate}
    >
      {t('dedicatedSpaceCheckbox')}
    </ForcedCheckbox>
  );
}

function MarketplaceLimit() {
  const { t } = useTranslation('input', { keyPrefix: 'businessType' });
  const [accept, setAccept] = useState(false);

  const validate = useCallback(
    () => (accept ? null : 'MarketplaceLimit is not checked'),
    [accept],
  );

  return (
    <div css={css({ display: 'flex', flexDirection: 'column', gap: 16 })}>
      <FlashMessage flavor={'warning'} message={t('mobileMessage')} />
      <ForcedCheckbox validate={validate} checked={accept} onChange={setAccept}>
        {t('mobileCheckbox')}
      </ForcedCheckbox>
    </div>
  );
}

function ShopAndFreelancerBusinessTypeCard(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'businessType' });
  const [isShopSelected, setIsShopSelected] = useState(false);
  const { data, setData } = useFormContext();
  const { businessType } = data;

  useEffect(() => {
    if (
      businessType &&
      businessType !== BusinessTypeType.SHOP &&
      businessType !== BusinessTypeType.FREELANCER
    ) {
      setIsShopSelected(false);
    }
  }, [businessType]);

  const onCategorySelect = useCallback(() => {
    setData(current => ({ ...current, businessType: null }));
    setIsShopSelected(true);
  }, [setData]);

  const onOptionSelect = useCallback(
    (option: BusinessTypeType) => {
      setData(current => ({ ...current, businessType: option }));
    },
    [setData],
  );

  return (
    <>
      <div className="col-sm-12">
        <CardHorizontal
          icon={IconName.TW_PRO_VENUE}
          text={
            <Trans
              t={t}
              i18nKey={BusinessTypeType.SHOP}
              components={[
                <span
                  className="aura--copy-sm"
                  css={css({
                    color: ProColors.navy[800],
                    fontWeight: 400,
                    textAlign: 'start',
                  })}
                />,
              ]}
            />
          }
          onClick={onCategorySelect}
          selected={
            isShopSelected ||
            businessType === BusinessTypeType.SHOP ||
            businessType === BusinessTypeType.FREELANCER
          }
          padding={16}
        />
      </div>
      {isShopSelected && (
        <>
          <div className="col-sm-6">
            <CardVertical
              icon={IconName.TW_PRO_PERSONS}
              text={
                <Trans
                  t={t}
                  i18nKey={'team'}
                  components={[
                    <span
                      className="aura--copy-sm"
                      css={css({
                        color: ProColors.navy[800],
                        textAlign: 'start',
                      })}
                    />,
                  ]}
                />
              }
              onClick={() => onOptionSelect(BusinessTypeType.SHOP)}
              selected={businessType === BusinessTypeType.SHOP}
              padding={16}
            />
          </div>
          <div className="col-sm-6">
            <CardVertical
              icon={IconName.TW_PRO_PERSON}
              text={
                <Trans
                  t={t}
                  i18nKey={'alone'}
                  components={[
                    <span
                      className="aura--copy-sm"
                      css={css({
                        color: ProColors.navy[800],
                        textAlign: 'start',
                      })}
                    />,
                  ]}
                />
              }
              onClick={() => onOptionSelect(BusinessTypeType.FREELANCER)}
              selected={businessType === BusinessTypeType.FREELANCER}
              padding={16}
            />
          </div>
        </>
      )}
    </>
  );
}

function BusinessTypeCard(props: BusinessTypeCardProps): JSX.Element {
  const { icon, type } = props;
  const { t } = useTranslation('input', { keyPrefix: 'businessType' });

  const {
    data: { businessType },
    setData,
  } = useFormContext();

  const onClick = useCallback(() => {
    setData(current => ({ ...current, businessType: type }));
  }, [type, setData]);

  return (
    <CardHorizontal
      icon={icon}
      text={
        <Trans
          t={t}
          i18nKey={type}
          components={[
            <span
              className="aura--copy-sm"
              css={css({
                color: ProColors.navy[800],
                fontWeight: 400,
                textAlign: 'start',
              })}
            />,
          ]}
        />
      }
      onClick={onClick}
      selected={businessType === type}
      padding={16}
    />
  );
}

export function BusinessType(props: {
  onError: (error: string | null) => void;
}): JSX.Element {
  const { onError } = props;
  const { t } = useTranslation('input', { keyPrefix: 'businessType' });
  const { data, setData } = useFormContext();

  const validate = useCallback(
    (data: FormContextData) =>
      data.businessType ? null : 'No BusinessType selected',
    [],
  );
  const wasValidated = useValidator(validate);
  const isValid = !validate(data);

  useEffect(() => {
    if (isValid) {
      onError(null);
    } else if (wasValidated) {
      onError(t('required'));
    }
  }, [onError, isValid, wasValidated, t]);

  useEffect(() => {
    if (data.businessType === BusinessTypeType.HOME) {
      setData(current => ({ ...current, dedicatedSpace: false }));
    } else {
      setData(current => ({ ...current, dedicatedSpace: null }));
    }
  }, [data.businessType, setData]);

  const chairRentalCountries = ['GB', 'IE'];
  const hasExtraBusinessTypes = chairRentalCountries.includes(
    getCountryCodeFromTLD(),
  );

  return (
    <div>
      <Grid css={css({ gap: 16 })}>
        {hasExtraBusinessTypes ? (
          <ShopAndFreelancerBusinessTypeCard />
        ) : (
          <div className="col-sm-12">
            <BusinessTypeCard
              icon={IconName.TW_PRO_VENUE}
              type={BusinessTypeType.SHOP}
            />
          </div>
        )}
        {hasExtraBusinessTypes && (
          <div className="col-sm-12">
            <BusinessTypeCard
              icon={IconName.TW_PRO_CHAIR}
              type={BusinessTypeType.CHAIR_RENTAL}
            />
          </div>
        )}
        <div className="col-sm-12">
          <BusinessTypeCard
            icon={IconName.TW_PRO_HOME}
            type={BusinessTypeType.HOME}
          />
        </div>
        {data.businessType === BusinessTypeType.HOME && (
          <div className="col-sm-12">
            <DedicatedSpace />
          </div>
        )}
        <div className="col-sm-12">
          <BusinessTypeCard
            icon={IconName.TW_PRO_MOBILE_THERAPIST}
            type={BusinessTypeType.MOBILE}
          />
        </div>
        {data.businessType === BusinessTypeType.MOBILE && (
          <div className="col-sm-12">
            <MarketplaceLimit />
          </div>
        )}
      </Grid>
    </div>
  );
}
